import { z } from 'zod';
import { QUESTION_TYPES } from './domain.js';

/**
 * Validierungsschemata für die Frage-DEFINITION (Anlegen/Bearbeiten/Import).
 * Als diskriminierte Union über `type` – erzwingt die typ-spezifischen Regeln
 * (Optionsanzahl, Korrektheit, Konfiguration) an einer einzigen Stelle.
 *
 * Wichtig: Dies validiert die Definition. Die serverseitige Auswertung der
 * gegebenen Antworten (Punkte) lebt separat in apps/api (Phase 3).
 */

export const optionInputSchema = z.object({
  label: z.string().trim().max(500).default(''),
  isCorrect: z.boolean().default(false),
  /** Optionales Antwortbild (lokaler /uploads-Pfad). */
  imagePath: z.string().max(500).nullish(),
});
export type OptionInput = z.infer<typeof optionInputSchema>;

const baseFields = {
  prompt: z.string().trim().min(1, 'Fragetext darf nicht leer sein').max(2000),
  imagePath: z.string().max(500).nullish(),
  timeLimitS: z.number().int().min(5).max(600).default(20),
  pointsFactor: z.number().min(0).max(10).default(1),
  explanation: z.string().max(2000).nullish(),
  /** Themen-Tags als Namen (werden serverseitig zu Tag-Verknüpfungen aufgelöst). */
  tags: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
};

// ─── Typ-spezifische Schemata ────────────────────────────────────────────────

const singleChoice = z.object({
  ...baseFields,
  type: z.literal('single_choice'),
  options: z
    .array(optionInputSchema)
    .min(2, 'Mindestens 2 Antworten')
    .max(6, 'Höchstens 6 Antworten'),
});

const multipleChoice = z.object({
  ...baseFields,
  type: z.literal('multiple_choice'),
  options: z.array(optionInputSchema).min(2).max(6),
  config: z
    .object({ partialCredit: z.boolean().default(true) })
    .default({ partialCredit: true }),
});

const trueFalse = z.object({
  ...baseFields,
  type: z.literal('true_false'),
  /** true => „Wahr" ist korrekt, false => „Falsch". */
  correct: z.boolean(),
});

const ordering = z.object({
  ...baseFields,
  type: z.literal('ordering'),
  /** Elemente in der KORREKTEN Reihenfolge (Array-Index = Sollposition). */
  items: z
    .array(z.string().trim().min(1).max(500))
    .min(2, 'Mindestens 2 Elemente')
    .max(8, 'Höchstens 8 Elemente'),
});

const matchPair = z.object({
  ...baseFields,
  type: z.literal('match_pair'),
  /** Zuordnungspaare; die rechte Spalte wird den Teilnehmenden gemischt angeboten. */
  pairs: z
    .array(
      z.object({
        left: z.string().trim().min(1).max(200),
        right: z.string().trim().min(1).max(200),
      }),
    )
    .min(2, 'Mindestens 2 Paare')
    .max(8, 'Höchstens 8 Paare'),
});

const openText = z.object({
  ...baseFields,
  type: z.literal('open_text'),
  config: z
    .object({
      // Voting erlaubt ⇒ Brainstorming-Folie (Beiträge werden nach Stimmen sortiert).
      allowUpvotes: z.boolean().default(false),
      maxEntriesPerParticipant: z.number().int().min(1).max(10).default(3),
    })
    .default({ allowUpvotes: false, maxEntriesPerParticipant: 3 }),
});

const qa = z.object({
  ...baseFields,
  type: z.literal('qa'),
  config: z
    .object({
      allowUpvotes: z.boolean().default(true),
      maxEntriesPerParticipant: z.number().int().min(1).max(20).default(5),
    })
    .default({ allowUpvotes: true, maxEntriesPerParticipant: 5 }),
});

const numericEstimate = z.object({
  ...baseFields,
  type: z.literal('numeric_estimate'),
  config: z.object({
    correctValue: z.number(),
    tolerance: z.number().min(0).default(0),
    toleranceType: z.enum(['absolute', 'percent']).default('absolute'),
  }),
});

const freeText = z.object({
  ...baseFields,
  type: z.literal('free_text'),
  config: z.object({
    /** Musterlösungen; Abgleich case-insensitive und leerzeichentolerant. */
    accepted: z
      .array(z.string().trim().min(1).max(300))
      .min(1, 'Mindestens eine Musterlösung'),
  }),
});

const poll = z.object({
  ...baseFields,
  type: z.literal('poll'),
  options: z.array(optionInputSchema.pick({ label: true, imagePath: true })).min(2).max(8),
  config: z.object({ allowMultiple: z.boolean().default(false) }).default({ allowMultiple: false }),
});

const wordcloud = z.object({
  ...baseFields,
  type: z.literal('wordcloud'),
  config: z
    .object({ maxEntriesPerParticipant: z.number().int().min(1).max(10).default(3) })
    .default({ maxEntriesPerParticipant: 3 }),
});

const scale = z.object({
  ...baseFields,
  type: z.literal('scale'),
  config: z
    .object({
      min: z.number().int().min(0).max(10).default(1),
      max: z.number().int().min(2).max(10).default(5),
      minLabel: z.string().max(60).nullish(),
      maxLabel: z.string().max(60).nullish(),
      /** Mehrere Aussagen/Abfragen (jede wird einzeln bewertet, farblich getrennt). Leer = eine Aussage (der Fragetext). */
      statements: z.array(z.string().trim().min(1).max(200)).max(8).default([]),
    })
    .default({ min: 1, max: 5, statements: [] }),
});

const imagePin = z.object({
  ...baseFields,
  type: z.literal('image_pin'),
  // Bild wird meist direkt nach dem Anlegen hochgeladen -> optional, damit die Folie
  // sofort angelegt werden kann. Vor dem Start sollte ein Bild gesetzt sein.
  imagePath: z.string().max(500).nullish(),
  config: z
    .object({
      targetX: z.number().min(0).max(100).default(50),
      targetY: z.number().min(0).max(100).default(50),
      radius: z.number().min(1).max(50).default(12),
    })
    .default({ targetX: 50, targetY: 50, radius: 12 }),
});

const ranking = z.object({
  ...baseFields,
  type: z.literal('ranking'),
  /** Zu ordnende Elemente (Reihenfolge egal – Teilnehmende priorisieren). */
  items: z.array(z.string().trim().min(1).max(500)).min(2, 'Mindestens 2 Elemente').max(8, 'Höchstens 8 Elemente'),
});

const spinner = z.object({
  ...baseFields,
  type: z.literal('spinner'),
  prompt: z.string().max(2000).default(''),
  timeLimitS: z.number().int().min(0).max(600).default(0),
  pointsFactor: z.number().min(0).max(10).default(0),
  config: z
    .object({
      entries: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
      useParticipants: z.boolean().default(false),
    })
    .default({ entries: [], useParticipants: false }),
});

const info = z.object({
  ...baseFields,
  type: z.literal('info'),
  // Infofolie: Überschrift optional (Inhalt kommt oft über frei platzierte Elemente),
  // keine Wertung, kein Zeitlimit.
  prompt: z.string().max(2000).default(''),
  timeLimitS: z.number().int().min(0).max(600).default(0),
  pointsFactor: z.number().min(0).max(10).default(0),
  // Optionales lokales Video (offline) → macht die Infofolie zur „Video-Folie".
  config: z
    .object({
      video: z
        .object({
          src: z.string().max(500),
          loop: z.boolean().default(false),
          muted: z.boolean().default(false),
        })
        .optional(),
    })
    .optional(),
});

const leaderboard = z.object({
  ...baseFields,
  type: z.literal('leaderboard'),
  // Bestenlisten-Folie: zeigt den aktuellen Zwischenstand; keine Wertung, kein Timer.
  prompt: z.string().max(2000).default('Zwischenstand'),
  timeLimitS: z.number().int().min(0).max(600).default(0),
  pointsFactor: z.number().min(0).max(10).default(0),
});

export const questionInputSchema = z
  .discriminatedUnion('type', [
    singleChoice,
    multipleChoice,
    trueFalse,
    ordering,
    matchPair,
    numericEstimate,
    freeText,
    poll,
    wordcloud,
    openText,
    qa,
    info,
    scale,
    ranking,
    imagePin,
    spinner,
    leaderboard,
  ])
  .superRefine((q, ctx) => {
    // Korrektheits-Regeln, die sich nicht in der Union-Struktur ausdrücken lassen.
    if (q.type === 'single_choice') {
      const correct = q.options.filter((o) => o.isCorrect).length;
      if (correct !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Genau eine Antwort muss richtig sein.',
          path: ['options'],
        });
      }
    }
    if (q.type === 'multiple_choice') {
      const correct = q.options.filter((o) => o.isCorrect).length;
      if (correct < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Mindestens eine Antwort muss richtig sein.',
          path: ['options'],
        });
      }
    }
  });

export type QuestionInput = z.infer<typeof questionInputSchema>;

/** Zur Laufzeit nützliche Liste, spiegelt QUESTION_TYPES für UI-Auswahl. */
export const questionTypeLabels: Record<(typeof QUESTION_TYPES)[number], string> = {
  single_choice: 'Single Choice',
  multiple_choice: 'Multiple Choice',
  true_false: 'Wahr / Falsch',
  ordering: 'Reihenfolge',
  match_pair: 'Zuordnung (Match)',
  numeric_estimate: 'Schätzfrage',
  free_text: 'Freitext',
  poll: 'Umfrage',
  wordcloud: 'Wordcloud',
  open_text: 'Open End (Feedback)',
  qa: 'Q&A',
  info: 'Infofolie',
  scale: 'Skala / Bewertung',
  ranking: 'Ranking / Priorisierung',
  image_pin: 'Pin aufs Bild',
  spinner: 'Glücksrad',
  leaderboard: 'Bestenliste / Zwischenstand',
};
